/* eslint-disable */
import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Button, Form, Select, Empty, Spin } from 'antd';
import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';

import { getProposalTitle, getSectionTitle } from 'helpers/proposal';
import { GET_IMPORT_PROPOSALS, GET_IMPORT_PROPOSAL } from 'graphql/queries/proposalQueries';
import { getUserId, proposalsImportVar } from 'graphql/cache';
import CloseIcon from 'components/Icons/CloseIcon';

import './ImportSectionModal.scss';

const { Option } = Select;

const ImportSectionModal = ({ visible, onCancel, onConfirm, proposal }) => {
  const uid = getUserId();

  const [form] = Form.useForm();

  const [searchProposal, setSearchProposal] = useState('');
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [proposalList, setProposalList] = useState([]);
  const [sectionList, setSectionList] = useState([]);
  const [cahcedProposalList, setCahcedProposalList] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursorPaging, setCursorPaging] = useState(null);

  const changeSections = ({ proposalSelected }) => {
    if (proposalSelected && proposalSelected.draft) {
      const sections = _.compact(
        _.map(proposalSelected.draft.sectionorder, (s, sectionIndex) => {
          if (s === 'milestones') {
            // Dont allowing import of old milestones
            return null;
          }
          if (s === 'pricing') {
            // Dont allowing import of old pricing
            return null;
          }
          const stitle = getSectionTitle({
            proposal: proposalSelected,
            sectionName: s,
            sectionIndex,
          });
          if (!stitle || !stitle.trim()) {
            return null;
          }
          return {
            v: s,
            t: stitle,
            c: [proposalSelected?.language?.toLowerCase() || 'english'],
          };
        })
      );

      setSectionList(sections);
      setSelectedSection(sections[0]);
    }
  };

  const [loadProposals, { fetchMore }] = useLazyQuery(GET_IMPORT_PROPOSALS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      processProposals(data?.fetchProposals);
    },
  });

  const [loadProposal, { loading: isFetchingProposal }] = useLazyQuery(GET_IMPORT_PROPOSAL, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.fetchProposal) {
        const proposal = data.fetchProposal;
        const ptitle = getProposalTitle({ proposal });
        if (!ptitle || !ptitle.trim()) {
          return null;
        }
        setSelectedProposal({
          v: proposal._id,
          draft: proposal.draft,
          t: ptitle,
          language: proposal?.language?.toLowerCase(),
          c: [proposal?.language?.toLowerCase() || 'english'],
        });
        changeSections({ proposalSelected: proposal });
      }
    },
  });

  const processProposals = (data, oldProposals = []) => {
    const { proposals, pagingMetadata } = data;

    setLoadingProposals(false);
    setLoadingMore(false);

    if (!proposals) {
      return;
    }

    if (searchProposal === '') {
      // cache the default list
      setCahcedProposalList([...oldProposals, ...proposals]);
    }

    setCursorPaging(pagingMetadata);
    const tempSortedList = proposals.slice().sort((a, b) => new Date(b.date) - new Date(a.date));

    proposalsImportVar(tempSortedList);

    const updatedProposalList = tempSortedList
      .map((p) => {
        if (p.version < 3 || !p.draft) return null;
        if (p._id === proposal._id) return null; // Dont select current proposal

        const ptitle = getProposalTitle({ proposal: p });
        if (!ptitle.trim()) return null;

        return {
          v: p._id,
          draft: p.draft, // TODO, change this in lexical
          t: ptitle,
          language: p?.language?.toLowerCase(),
          c: [p?.language?.toLowerCase() || 'english'],
        };
      })
      .filter(Boolean);

    setProposalList([...oldProposals, ...updatedProposalList]);
  };

  useEffect(() => {
    setLoadingProposals(true);
    loadProposals({
      variables: {
        uid,
        limit: 10,
        search: searchProposal,
      },
    });
  }, []);

  const onSearch = (value) => {
    setSearchProposal(value);

    if (value === '' && cahcedProposalList.length) {
      // try to serve the dfault list from cache
      processProposals(cahcedProposalList);
    } else {
      debouncedSearch(value);
    }
  };

  const debouncedSearch = useMemo(
    () =>
      _.debounce((value) => {
        setLoadingProposals(true);
        loadProposals({
          variables: {
            uid,
            limit: 10,
            search: value,
          },
        });
      }, 1000),
    [uid]
  );

  const onChangeProposal = (value) => {
    const changedProposal = proposalList.find((item) => item.v === value);
    setSelectedProposal(changedProposal);
    loadProposal({
      variables: {
        _id: changedProposal.v,
      },
    });
  };

  const onChangeSection = (value) => {
    const changedSection = sectionList.find((item) => item.v === value);
    setSelectedSection(changedSection);
  };

  const handlePopupScroll = async (e) => {
    const { scrollTop, offsetHeight, scrollHeight } = e.target;
    const nearBottom = scrollTop + offsetHeight >= scrollHeight - 20;

    if (!nearBottom || loadingMore || !cursorPaging?.hasNext) {
      return;
    }

    setLoadingMore(true);

    const { data } = await fetchMore({
      variables: {
        uid,
        limit: 10,
        search: searchProposal,
        cursorPaging: cursorPaging?.cursor,
      },
    });

    processProposals(data?.fetchProposals, proposalList);
  };

  return (
    <Modal
      className="import-section-modal"
      visible={visible}
      centered
      okText="CONTINUE"
      closeIcon={<CloseIcon className="close-icon" />}
      cancelText="CANCEL"
      onCancel={onCancel}
      footer={null}>
      <h3 className="title">Import Section</h3>
      <div className="divider" />
      <div className="body">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            proposalName: selectedProposal ? selectedProposal.v : null,
            sectionName: selectedSection ? selectedSection.v : null,
          }}>
          <Form.Item name="proposalName" label="PROPOSAL NAME">
            <Select
              showSearch
              allowClear={!loadingProposals}
              loading={loadingProposals}
              placeholder="Select a proposal"
              dropdownClassName="import-section"
              value={selectedProposal}
              onSearch={onSearch}
              onChange={onChangeProposal}
              filterOption={false}
              onPopupScroll={handlePopupScroll}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  {loadingMore && (
                    <div className="loading-more">
                      <Spin />
                    </div>
                  )}
                </>
              )}>
              {proposalList.map((item) => (
                <Option key={item.v} value={item.v}>
                  {item.t}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="sectionName" label="SECTION NAME">
            <Select
              maxTagPlaceholder={selectedProposal ? 'Select Section' : 'Select Proposal'}
              onChange={onChangeSection}
              loading={isFetchingProposal}>
              {sectionList.length ? (
                sectionList.map((item) => {
                  return (
                    <Option key={item.v} value={item.v}>
                      {item.t}
                    </Option>
                  );
                })
              ) : (
                <Option>
                  {' '}
                  <Empty description={'Select Proposal'} />
                </Option>
              )}
            </Select>
          </Form.Item>
        </Form>
      </div>
      <div className="button-wrapper">
        <Button
          className="large-btn secondary-btn"
          disabled={loadingProposals}
          onClick={() => {
            onConfirm({ importedSection: selectedProposal.draft[selectedSection.v] });
          }}>
          IMPORT
        </Button>
        <Button className="large-btn grey-btn" onClick={onCancel}>
          CANCEL
        </Button>
      </div>
    </Modal>
  );
};

ImportSectionModal.defaultProps = {
  visible: false,
};
ImportSectionModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  visible: PropTypes.bool,
  proposal: PropTypes.shape({
    _id: PropTypes.string.isRequired,
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default ImportSectionModal;
